// @spec SHL-STR-018
'use client';

import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DateRangeDropdown } from './DateRangeDropdown';
import { SearchFilterChip } from './SearchFilterChip';

import type { DateRangeFilter } from '@/api/search';
import { Button } from '@/components/ui/button';

export interface SearchFiltersProps {
  /** Available filter categories */
  availableCategories: { name: string; displayName: string }[];
  /** Currently selected categories */
  selectedCategories: string[];
  /** Toggle a category filter */
  onToggleCategory: (category: string) => void;
  /** Current date range filter */
  dateRange: DateRangeFilter | null;
  /** Set date range filter */
  onDateRangeChange: (range: DateRangeFilter | null) => void;
  /** Clear all filters */
  onClearAll: () => void;
  /** Whether any filters are active */
  hasActiveFilters: boolean;
}

/**
 * Filter chips component for search results.
 *
 * Displays category filter chips and a date range dropdown.
 * Shows a "Clear all" button when filters are active.
 *
 * @example
 * <SearchFilters
 *   availableCategories={availableCategories}
 *   selectedCategories={selectedCategories}
 *   onToggleCategory={toggleCategory}
 *   dateRange={dateRange}
 *   onDateRangeChange={setDateRange}
 *   onClearAll={clearFilters}
 *   hasActiveFilters={hasActiveFilters}
 * />
 */
export function SearchFilters({
  availableCategories,
  selectedCategories,
  onToggleCategory,
  dateRange,
  onDateRangeChange,
  onClearAll,
  hasActiveFilters,
}: SearchFiltersProps) {
  const { t } = useTranslation('common');
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
      {/* Category filters */}
      <div className="flex flex-wrap items-center gap-1.5">
        {availableCategories.map((category) => (
          <SearchFilterChip
            key={category.name}
            label={category.displayName}
            isSelected={selectedCategories.includes(category.name)}
            onToggle={() => {
              onToggleCategory(category.name);
            }}
          />
        ))}
      </div>

      {/* Separator */}
      <div className="h-4 w-px bg-border" />

      {/* Date range filter */}
      <DateRangeDropdown value={dateRange} onChange={onDateRangeChange} />

      {/* Clear all button */}
      {hasActiveFilters && (
        <>
          <div className="flex-1" />
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAll}
            className="h-6 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <XIcon className="h-3 w-3" />
            {t('buttons.clear')}
          </Button>
        </>
      )}
    </div>
  );
}
